import React, { useEffect, useState } from 'react';

import { toast } from 'react-toastify';

import { Card, Stack } from '@mui/material';

import RemoveModal from './components/RemoveModal';
import UploadModal from './components/UploadModal';
import * as S from './styles';
import Button from 'components/Button';
import Text from 'components/Text';
import { useEditStatus } from 'global/EditStatusContext';
import { useTab } from 'global/TabContext';
import { useUser } from 'global/UserContext';
import useProfileImage from 'pages/SettingsProfile/hooks/useProfileImage';
import { AvatarDefault } from 'utils/utilsIcons';

import 'react-toastify/dist/ReactToastify.css';

interface ProfileImageProps {
  onClose: () => void;
}

const ProfileImage: React.FC<ProfileImageProps> = ({ onClose }) => {
  const { activeTabContext } = useTab();
  const { user } = useUser();
  const { isEditing } = useEditStatus();

  const [userFirstName, setUserFirstName] = useState(user?.first_name);
  const [userLastName, setUserLastName] = useState(user?.last_name);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  const [showSavingStatus, setShowSavingStatus] = useState(isEditing);

  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { profileImage, updateProfileImage, deleteProfileImage, loading } = useProfileImage(
    user?.id || ''
  );

  useEffect(() => {
    if (user) {
      setUserFirstName(user?.first_name);
      setUserLastName(user?.last_name);
    }
  }, [user]);

  useEffect(() => {
    if (isEditing) {
      setShowSavingStatus(true);
    } else {
      const timer = setTimeout(() => {
        setShowSavingStatus(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isEditing]);

  const handleUpload = (info: any) => {
    const file = info.file;
    if (!file) {
      toast.error('Nenhum arquivo foi selecionado.');
      return;
    }
    if (!file.type || !file.type.startsWith('image/')) {
      toast.error('Por favor, envie um arquivo de imagem válido.');
      return;
    }

    const maxFileSize = 1024 * 1024 * 5; // 5MB

    const img = new Image();
    const reader = new FileReader();

    reader.onload = e => {
      img.src = e.target?.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const MAX_WIDTH = 1024; // Maximum width (adjust as needed)
        const MAX_HEIGHT = 1024; // Maximum height (adjust as needed)
        let width = img.width;
        let height = img.height;

        // Calculate the new width and height based on max constraints
        if (width > height) {
          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = Math.round((width * MAX_HEIGHT) / height);
            height = MAX_HEIGHT;
          }
        }

        // Resize the image
        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);

        // Convert the resized canvas to a Blob
        canvas.toBlob(
          blob => {
            if (blob && blob.size <= maxFileSize) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });

              setSelectedFile(resizedFile);
              setPreviewImage(URL.createObjectURL(resizedFile));
            } else {
              toast.error('A imagem não pôde ser redimensionada abaixo de 500 KB.');
            }
          },
          file.type,
          0.8 // Quality factor for compression (0.0 to 1.0)
        );
      };
    };

    reader.readAsDataURL(file);

    // setSelectedFile(file);
    // setPreviewImage(URL.createObjectURL(file));
  };

  const handleConfirmUpload = () => {
    if (selectedFile) {
      updateProfileImage(selectedFile);
      setPreviewImage(null);
      setSelectedFile(null);
      toast.success('Foto de perfil atualizada com sucesso!');
    } else {
      toast.error('Nenhum arquivo selecionado.');
    }
  };

  const handleCancelUpload = () => {
    setPreviewImage(null);
    setSelectedFile(null);
    setIsModalVisible(false);
  };

  const handleRemoveImage = () => {
    deleteProfileImage();
    toast.success('Foto de perfil removida com sucesso!');
    setIsRemoveModalVisible(false);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleCloseRemoveModal = () => {
    setIsRemoveModalVisible(false);
  };

  const renderAvatarOrImage = () => {
    if (previewImage) {
      return (
        <S.PreviewImage>
          <Text size="medium" bold>
            Pré-visualização:
          </Text>
          <img
            src={previewImage}
            alt="Preview"
            style={{ width: 180, height: 180, borderRadius: '50%' }}
          />
        </S.PreviewImage>
      );
    }

    if (profileImage) {
      return (
        <img
          src={profileImage}
          alt={userFirstName}
          style={{ width: 180, height: 180, borderRadius: '50%' }}
        />
      );
    }
    return <S.ProfilePicture src={AvatarDefault} alt={userFirstName} />;
  };

  return (
    <Card sx={{ width: '100%', height: 'fit-content', borderRadius: 2 }}>
      <Text size="medium" bold color="textPrimary">
        Foto de perfil
      </Text>
      <Stack
        width="100%"
        direction="row"
        spacing={2}
        sx={{ padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}
      >
        <S.ProfilePictureWrapper>{renderAvatarOrImage()}</S.ProfilePictureWrapper>

        <Stack direction="column" spacing={1} width="50%">
          <Button dottedBorder underlineText onClick={() => setIsModalVisible(true)}>
            Alterar foto de perfil
          </Button>
          <Text size="xSmall" color="textSecondary" textAlign="left">
            O arquivo deve conter no máximo 1MB e ser no formato .jpg ou .png
          </Text>
        </Stack>
      </Stack>

      <UploadModal
        isModalVisible={isModalVisible}
        loading={loading}
        previewImage={previewImage}
        profileImage={profileImage}
        handleCancelUpload={handleCancelUpload}
        handleConfirmUpload={handleConfirmUpload}
        handleUpload={handleUpload}
      />

      <RemoveModal
        isRemoveModalVisible={isRemoveModalVisible}
        loading={loading}
        profileImage={profileImage}
        handleRemoveImage={handleRemoveImage}
        handleCloseRemoveModal={handleCloseRemoveModal}
      />
    </Card>
  );
};

export default ProfileImage;
